import {
  BorderType,
  BrandType,
  ContentType,
  PaletteColor,
  SurfaceGradientType,
  SurfaceType,
  TextAndIconType,
  TypeBackground,
  TypeText,
} from '@mui/material/styles';
import { ColorPartial, PaletteOptions } from '@mui/material/styles/createPalette';

import * as colors from './colors';

declare module '@mui/material/styles' {
  interface BrandType {
    tapsi: string;
    driver: string;
  }

  interface SurfaceGradientType {
    brand: string;
    primaryWallet: string;
    secondaryWallet: string;
    brandLight?: string;
    blue?: string;
    red?: string;
    grey?: string;
    white?: string;
    tip?: string;
  }
  interface BackgroundGradientType {
    whiteToGrey?: string;
  }
  interface NPSGradientType {
    1_4?: string;
    5_6?: string;
    7_8?: string;
    9_18?: string;
  }
  interface PaletteOptions {
    textAndIcon: TextAndIconType;
    border: BorderType;
    surface: SurfaceType;
    content: ContentType;
    brand: BrandType;
    surfaceGradient: SurfaceGradientType;
    vendorPrimary: Palette['primary'];
    backgroundGradient?: BackgroundGradientType;
    NPSGradient?: NPSGradientType;
    neutral: ColorPartial;
    orange: ColorPartial;
    blue: ColorPartial;
    green: ColorPartial;
    yellow: ColorPartial;
    red: ColorPartial;
    indigo: ColorPartial;
    purple: ColorPartial;
  }

  interface SurfaceType {
    primary: string;
    secondary: string;
    warning: string;
    warningLight: string;
    tertiary: string;
    backgroundPrimary: string;
    backgroundSecondary: string;
    disable: string;
    inversePrimary: string;
    inverseSecondary: string;
    accent: string;
    negative: string;
    positive: string;
    accentLight: string;
    negativeLight: string;
    positiveLight: string;
    overlayDark: string;
    overlayLight: string;
    alwaysBlack: string;
    alwaysWhite: string;
  }
  interface ContentType {
    primary: string;
    secondary: string;
    tertiary: string;
    disable: string;
    onInverse: string;
    accent: string;
    accentLight: string;
    negative: string;
    warning: string;
    positive: string;
    onAccent: string;
    onNegative: string;
    onWarning: string;
    onPositive: string;
    onBrand: string;
  }
  interface PaletteColor {
    lighter?: string;
    darker?: string;
  }

  interface TypeBackground {
    neutral: string;
    light: string;
    lightest: string;
    dark: string;
    darker: string;
    darkest: string;
  }
  interface Palette {
    vendorPrimary: Palette['primary'];
    surface: SurfaceType;
    brand: BrandType;
    content: ContentType;
    textAndIcon: TextAndIconType;
    border: BorderType;
    neutral: ColorPartial;
    orange: ColorPartial;
    blue: ColorPartial;
    green: ColorPartial;
    yellow: ColorPartial;
    red: ColorPartial;
    indigo: ColorPartial;
    purple: ColorPartial;
    backgroundGradient?: BackgroundGradientType;
  }
  interface PaletteOptions {
    vendorPrimary: Palette['primary'];
    surface: SurfaceType;
    NPSGradient?: NPSGradientType;
    surfaceGradient: SurfaceGradientType;
    content: ContentType;
    textAndIcon: TextAndIconType;
    border: BorderType;
    neutral: ColorPartial;
    orange: ColorPartial;
    blue: ColorPartial;
    green: ColorPartial;
    yellow: ColorPartial;
    red: ColorPartial;
    indigo: ColorPartial;
    purple: ColorPartial;
  }

  interface TextAndIconType {
    light: string;
    lighter: string;
    lightest: string;
    dark: string;
    darker: string;
  }

  interface BorderType {
    light: string;
    lighter: string;
    lightest: string;
    dark: string;
    darker: string;
  }
}

const surface: SurfaceType = {
  primary: colors.grey.white,
  secondary: colors.grey[50],
  tertiary: colors.grey[100],
  backgroundPrimary: colors.grey[50],
  backgroundSecondary: colors.grey[100],
  disable: colors.grey[50],
  inversePrimary: colors.grey.black,
  inverseSecondary: colors.grey[700],
  accent: colors.blue[400],
  negative: colors.red[400],
  warning: colors.yellow[400],
  positive: colors.green[400],
  accentLight: colors.blue[50],
  negativeLight: colors.red[50],
  warningLight: colors.yellow[50],
  positiveLight: colors.green[50],
  alwaysBlack: colors.grey.black,
  alwaysWhite: colors.grey.white,
  overlayDark: `${colors.grey.black}4D`,
  overlayLight: `${colors.grey.black}1F`,
};

const content: ContentType = {
  primary: colors.grey.black,
  secondary: colors.grey[600],
  tertiary: colors.grey[500],
  disable: colors.grey[400],
  onInverse: colors.grey.white,
  accent: colors.blue[400],
  accentLight: colors.blue[50],
  negative: colors.red[400],
  warning: colors.yellow[500],
  positive: colors.green[500],
  onAccent: colors.grey.white,
  onBrand: colors.grey.white,
  onNegative: colors.grey.white,
  onPositive: colors.grey.white,
  onWarning: colors.grey.black,
};
const surfaceGradient: SurfaceGradientType = {
  brand:
    'linear-gradient(90deg, rgba(255,119,51,1) 30%, rgba(255,87,34,1) 80%, rgba(229,73,23,1) 100%)',
  primaryWallet: 'linear-gradient(90deg, #FBBA7E 0%, #CB91AD 100%)',
  secondaryWallet: 'linear-gradient(90deg, rgba(176,153,184,1) 67%, rgba(141,185,191,1) 100%)',
};
const grey: ColorPartial = {
  50: '#F5F7F7',
  100: '#F0F3F5',
  200: '#E1E3E3',
  300: '#ABAFB2',
  500: '#8D9399',
  700: '#676E73',
  900: '#40464D',
};

const common = {
  black: '#2B2F33',
  white: '#FFFFFF',
  iconLight: colors.neutral[600],
  iconDarker: colors.neutral[800],
};

const background: TypeBackground = {
  default: '#F2F3F4',
  neutral: '#F2F3F4',
  paper: '#FFFFFF',
  lightest: colors.neutral[50],
  light: colors.neutral[100],
  dark: colors.neutral[200],
  darker: colors.neutral[300],
  darkest: colors.neutral[900],
};

const primary: PaletteColor = {
  main: colors.primary[500],
  lighter: colors.primary[50],
  light: colors.primary[100],
  dark: colors.primary[600],
  darker: colors.primary[800],
  contrastText: common.white,
};

const secondary: PaletteColor = {
  main: colors.secondary[500],
  lighter: colors.secondary[50],
  light: colors.secondary[100],
  dark: colors.secondary[600],
  darker: colors.secondary[800],
  contrastText: common.white,
};

const error: PaletteColor = {
  main: colors.error[500],
  lighter: colors.error[50],
  light: colors.error[100],
  dark: colors.error[600],
  darker: colors.error[800],
  contrastText: common.white,
};

const success: PaletteColor = {
  main: colors.tertiary[500],
  lighter: colors.tertiary[50],
  light: colors.tertiary[100],
  dark: colors.tertiary[600],
  darker: colors.tertiary[800],
  contrastText: common.white,
};

const warning: PaletteColor = {
  main: '#CC6214',
  lighter: '#FFF2E5',
  light: '#E5955C',
  dark: '#803D0C',
  contrastText: common.white,
};

const info: PaletteColor = {
  main: colors.info[500],
  lighter: colors.info[50],
  light: colors.info[100],
  dark: colors.info[600],
  darker: colors.info[800],
  contrastText: common.white,
};

const vendorPrimary: PaletteColor = {
  main: colors.vPrimary[500],
  lighter: colors.vPrimary[50],
  light: colors.vPrimary[100],
  dark: colors.vPrimary[600],
  darker: colors.vPrimary[800],
  contrastText: common.white,
};

const text: TypeText = {
  primary: '#2B2F33',
  secondary: '#4F565C',
  disabled: '#B6BCC2',
};

const textAndIcon: TextAndIconType = {
  lightest: colors.neutral[50],
  lighter: colors.neutral[200],
  light: colors.neutral[500],
  dark: colors.neutral[700],
  darker: colors.neutral[900],
};

const border: BorderType = {
  lightest: colors.neutral[100],
  lighter: colors.neutral[200],
  light: colors.neutral[300],
  dark: colors.neutral[500],
  darker: colors.neutral[700],
};
const brand: BrandType = {
  driver: colors.blue[400],
  tapsi: colors.orange[400],
};

const defaultLightPalette: PaletteOptions = {
  mode: 'light',
  primary,
  secondary,
  grey,
  error,
  info,
  warning,
  common,
  background,
  text,
  contrastThreshold: 3,
  tonalOffset: 0.2,
  success,
  vendorPrimary,
  divider: 'rgba(0,0,0,0,0.12)',
  textAndIcon,
  border,
  surface,
  content,
  surfaceGradient,
  neutral: colors.neutral,
  orange: colors.orange,
  blue: colors.blue,
  green: colors.green,
  indigo: colors.indigo,
  purple: colors.purple,
  red: colors.red,
  yellow: colors.yellow,
  brand,
};

export default defaultLightPalette;
